"use client";

import type { AccountTier } from "@/lib/types";
import { useEffect, useRef, useState } from "react";

interface AdSenseAdProps {
  readonly adSlot: string; // Ad unit ID from AdSense dashboard
  readonly adFormat?: "auto" | "rectangle" | "vertical" | "horizontal";
  readonly fullWidthResponsive?: boolean;
  readonly accountTier?: AccountTier;
  readonly className?: string;
}

interface WindowWithAds extends Window {
  adsbygoogle?: Record<string, unknown>[];
}

const ADSENSE_PUBLISHER_ID = "ca-pub-3586023067825968";
const ADSENSE_ENABLED = true; // Can be controlled via env var if needed

export function AdSenseAd({
  adSlot,
  adFormat = "auto",
  fullWidthResponsive = true,
  accountTier,
  className = "",
}: AdSenseAdProps) {
  const adContainerRef = useRef<HTMLDivElement>(null);
  const adPushedRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [adReady, setAdReady] = useState(false);

  // Only show ads for free tier users
  const isFreeTier = accountTier === "free" || !accountTier;
  const shouldShowAd = ADSENSE_ENABLED && isFreeTier;

  useEffect(() => {
    if (!shouldShowAd || globalThis.window === undefined) {
      return;
    }

    // Cleanup function
    const cleanup = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };

    // Check if ad is already initialized
    const checkIfInitialized = (): boolean => {
      if (adPushedRef.current) {
        return true;
      }
      if (!adContainerRef.current) {
        return false;
      }
      const insElement = adContainerRef.current.querySelector(".adsbygoogle") as HTMLElement;
      return insElement?.dataset.adsbygoogleStatus !== undefined;
    };

    // Initialize the ad
    const initializeAd = () => {
      // Check if already initialized
      if (checkIfInitialized()) {
        cleanup();
        return;
      }

      if (!adContainerRef.current) {
        return;
      }

      const insElement = adContainerRef.current.querySelector(".adsbygoogle") as HTMLElement;
      if (!insElement) {
        return;
      }

      // Check if ad has already been initialized
      if (insElement.dataset.adsbygoogleStatus) {
        adPushedRef.current = true;
        setAdReady(true);
        cleanup();
        return;
      }

      // Check container dimensions
      const rect = adContainerRef.current.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return;
      }

      // Check if AdSense script is loaded
      if (!(globalThis.window as unknown as WindowWithAds).adsbygoogle) {
        return;
      }

      // Push ad to AdSense queue
      try {
        const adsbygoogle = (globalThis.window as unknown as WindowWithAds).adsbygoogle || [];
        (globalThis.window as unknown as WindowWithAds).adsbygoogle = adsbygoogle;
        adsbygoogle.push({});
        adPushedRef.current = true;
        setAdReady(true);
        cleanup();
      } catch (error) {
        // Handle "already have ads" error silently
        if (error instanceof Error && error.message.includes("already have ads")) {
          adPushedRef.current = true;
          setAdReady(true);
          cleanup();
        }
      }
    };

    // Try to initialize after a delay
    const tryInitialize = () => {
      if (checkIfInitialized()) {
        cleanup();
        return;
      }
      
      // Check if container has dimensions before attempting initialization
      if (adContainerRef.current) {
        const rect = adContainerRef.current.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
          // Container doesn't have dimensions yet, retry after a short delay
          if (!timeoutRef.current) {
            timeoutRef.current = setTimeout(tryInitialize, 300);
          }
          return;
        }
      }
      
      initializeAd();
      
      // If script not loaded yet, retry
      if (!checkIfInitialized() && !(globalThis.window as unknown as WindowWithAds).adsbygoogle) {
        if (!timeoutRef.current) {
          timeoutRef.current = setTimeout(tryInitialize, 300);
        }
      } else if (checkIfInitialized()) {
        // Successfully initialized, cleanup
        cleanup();
      }
    };

    // Initial attempt after DOM is ready - give script time to load
    timeoutRef.current = setTimeout(tryInitialize, 1000);

    // Use IntersectionObserver to initialize when visible
    if (adContainerRef.current && "IntersectionObserver" in globalThis.window && !checkIfInitialized()) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && !checkIfInitialized() && !adPushedRef.current) {
              initializeAd();
              // Disconnect immediately after attempting initialization
              if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
              }
              break;
            }
          }
        },
        { threshold: 0.1 }
      );

      observerRef.current.observe(adContainerRef.current);
    }

    return cleanup;
  }, [shouldShowAd]);

  // Monitor when ad is actually loaded and ready
  useEffect(() => {
    if (!shouldShowAd || !adContainerRef.current) {
      return;
    }

    const checkAdStatus = () => {
      const insElement = adContainerRef.current?.querySelector(".adsbygoogle") as HTMLElement;
      if (insElement?.dataset.adsbygoogleStatus === "done") {
        setAdReady(true);
      } else if (insElement?.dataset.adsbygoogleStatus === "filled") {
        setAdReady(true);
      }
    };

    // Check periodically until ad is ready
    const intervalId = setInterval(checkAdStatus, 500);

    // Stop checking after 10 seconds
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
    }, 10000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [shouldShowAd]);

  if (!shouldShowAd) {
    return null;
  }

  return (
    <div 
      ref={adContainerRef} 
      className={`adsense-container ${className}`}
      style={{ 
        visibility: adReady ? "visible" : "hidden",
        minHeight: "100px" // Keep min-height to ensure dimensions exist for getBoundingClientRect()
      }}
    >
      <ins
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={ADSENSE_PUBLISHER_ID}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive={fullWidthResponsive ? "true" : "false"}
      />
    </div>
  );
}

